<template>
  <v-row v-if="canHaveIntegrations">
    <v-col cols="12">
      <h2 class="text-h2 mt-10 mb-7">
        {{ $t("generalSettingsStrings.integrationSettingsTableTitle") }}
      </h2>
      <v-data-table
        density="compact"
        hide-default-footer
        :items="orgConfiguration.integrationSettings"
        :items-per-page="-1"
        :headers="integrationSettingsTableHeaders"
      >
        <template #item.integrationConfigurationId="{ item }">
          <vee-field
            v-slot="{ componentField, errors }"
            v-model="item.integrationConfigurationId"
            rules="required"
            :name="
              $t(
                'generalSettingsStrings.tableHeaders.integrationConfiguration'
              )
            "
          >
            <v-select
              v-bind="componentField"
              :error-messages="errors"
              density="compact"
              variant="outlined"
              :items="availableIntegrationConfigurations"
              :label="
                $t(
                  'generalSettingsStrings.placeholders.integrationConfiguration'
                )
              "
            />
          </vee-field>
        </template>

        <template #item.canUseSalaryFraction="{ item }">
          <v-checkbox
            v-model="item.canUseSalaryFraction"
            color="primary"
            :label="
              $t('generalSettingsStrings.tableHeaders.salaryFraction')
            "
            :disabled="!item.integrationConfigurationId"
          />
        </template>
        <template #item.importSalaryClaimsOnly="{ item }">
          <v-checkbox
            v-model="item.importSalaryClaimsOnly"
            color="primary"
            :label="
              $t(
                'generalSettingsStrings.tableHeaders.importSalaryClaimsOnly'
              )
            "
            :disabled="!item.integrationConfigurationId"
          />
        </template>
        <template
          #item.immutableSalaryClaimsCollection="{ item }"
        >
          <v-checkbox
            v-model="item.immutableSalaryClaimsCollection"
            color="primary"
            :label="
              $t(
                'generalSettingsStrings.labels.immutableSalaryClaimsCollection'
              )
            "
            :disabled="!item.integrationConfigurationId"
          />
        </template>
        <template #item.editableSalaryClaimPayingOrgs="{ item }">
          <v-checkbox
            v-model="item.editableSalaryClaimPayingOrgs"
            color="primary"
            :label="
              $t(
                'generalSettingsStrings.tableHeaders.editableSalaryClaimPayingOrgs'
              )
            "
            :disabled="!item.integrationConfigurationId"
          />
        </template>
        <template #item.actions="{ item }">
          <v-btn
            :disabled="disableCriticalSettings"
            rounded
            size="small"
            class="accent-inverted-button"
            @click="removeIntegration(item)"
          >
            {{ $t("commonStrings.remove") }}
          </v-btn>
        </template>
      </v-data-table>

      <v-col cols="12">
        <v-btn
          v-if="notUsedIntegrationConfigurations.length"
          rounded
          class="primary-inverted-button"
          @click="addIntegration"
        >
          <v-icon
            tag="span"
            start
          >
            mdi-plus
          </v-icon>
          {{ $t("generalSettingsStrings.addIntegration") }}
        </v-btn>
        <em v-else>{{
          $t("generalSettingsStrings.allIntegrationsUsed")
        }}</em>
      </v-col>
    </v-col>
  </v-row>
</template>

<script>
import { Field as VeeField } from "vee-validate";
import OrgTypes from "@/enums/OrgTypes";

export default {
  name: "IntegrationSettings",
  components: {
    VeeField
  },
  props: {
    orgConfiguration: {
      required: true,
      validator: prop => typeof prop === "object" || !prop
    },
    integrationConfigurations: Array,
    orgTypeId: Number,
    disableCriticalSettings: Boolean
  },
  data() {
    return {
      integrationSettingsTableHeaders: [
        {
          text: this.$t(
            "generalSettingsStrings.tableHeaders.integrationConfiguration"
          ),
          value: "integrationConfigurationId",
          sortable: false,
          filterable: false
        },
        {
          text: this.$t("generalSettingsStrings.tableHeaders.salaryFraction"),
          value: "canUseSalaryFraction",
          sortable: false,
          filterable: false
        },
        {
          text: this.$t(
            "generalSettingsStrings.tableHeaders.importSalaryClaimsOnly"
          ),
          value: "importSalaryClaimsOnly",
          sortable: false,
          filterable: false
        },
        {
          text: this.$t(
            "generalSettingsStrings.tableHeaders.immutableSalaryClaimsCollection"
          ),
          value: "immutableSalaryClaimsCollection",
          sortable: false,
          filterable: false
        },
        {
          text: this.$t(
            "generalSettingsStrings.tableHeaders.editableSalaryClaimPayingOrgs"
          ),
          value: "editableSalaryClaimPayingOrgs",
          sortable: false,
          filterable: false
        },
        {
          value: "actions",
          sortable: false,
          filterable: false
        }
      ]
    };
  },
  computed: {
    canHaveIntegrations() {
      return this.orgTypeId === OrgTypes.SF;
    },
    notUsedIntegrationConfigurations() {
      const usedIntegrationConfigurationIds = this.orgConfiguration.integrationSettings.map(
        x => x.integrationConfigurationId
      );

      return this.availableIntegrationConfigurations.filter(
        x => !usedIntegrationConfigurationIds.includes(x.value)
      );
    },
    availableIntegrationConfigurations() {
      return this.integrationConfigurations
        .filter(x => x.preferences.mandatoryFederationId)
        .map(function (x) {
          return {
            text: `${x.name} (${x.applicationId})`,
            value: x.id
          };
        });
    }
  },
  methods: {
    addIntegration: function () {
      this.orgConfiguration.integrationSettings.push({
        integrationConfigurationId: null,
        canUseSalaryFraction: false,
        importSalaryClaimsOnly: false,
        immutableSalaryClaimsCollection: false,
        editableSalaryClaimPayingOrgs: false
      });
    },
    removeIntegration: function (item) {
      const index = this.orgConfiguration.integrationSettings.indexOf(item);

      this.orgConfiguration.integrationSettings.splice(index, 1);
    }
  }
};
</script>